/**
 * Page Nos Partenaires & Écosystème - FTPE Outremer
 */
import React from 'react';
import { Handshake, Building2, Shield, Briefcase, Users, ArrowRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

// Logo FTPE Outremer
const FTPE_LOGO = "https://customer-assets.emergentagent.com/job_80d9c916-7281-4428-9c84-a6e0cd256c92/artifacts/ojac2aim_logo%20ftpe%20outremer%20dans%20fond%20blanc.png";

export default function FTPEPartenairesPage() {
  const navigate = useNavigate();

  const partenaires = [
    {
      icon: Building2,
      title: "Partenariats institutionnels",
      description: "Dialogue avec les administrations, collectivités et organismes publics.",
      note: null
    },
    {
      icon: Shield,
      title: "Dispositifs solidaires (COPPAM)",
      description: "Orientation vers des dispositifs de prévention et de sécurisation.",
      note: "Sous conditions propres à chaque dispositif."
    },
    {
      icon: Briefcase,
      title: "Services & accompagnements (O'SCOP)",
      description: "Accès facilité à des services d'ingénierie et d'outillage.",
      note: "La FTPE n'en assure pas l'exploitation."
    },
    {
      icon: Users,
      title: "Experts & ESS",
      description: "Réseau d'acteurs engagés, responsables et qualifiés.",
      note: null
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 text-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <BackButton variant="dark" label="Retour" />
          </div>
          
          <div className="flex items-center gap-6 mb-8">
            <img src={FTPE_LOGO} alt="FTPE Outremer" className="w-20 h-20 object-contain rounded-lg bg-white p-2" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Nos partenaires & <span className="text-amber-400">écosystème</span>
              </h1>
              <p className="text-xl text-slate-300">Un réseau structuré au service des employeurs ultramarins</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-5xl mx-auto bg-white shadow-lg -mt-10 relative z-10 rounded-2xl p-8 md:p-12 mb-12">
        
        {/* Rôle de facilitateur */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-3">
            <Handshake className="w-6 h-6" />
            Un rôle de facilitateur
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            La FTPE Outremer agit comme facilitateur et orienteur vers un écosystème
            de partenaires institutionnels, techniques et solidaires,
            dans le respect strict de son rôle syndical.
          </p>
        </section>

        {/* Partenaires */}
        <section className="mb-12">
          <div className="grid md:grid-cols-2 gap-6">
            {partenaires.map((partenaire, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border-l-4 border-amber-500">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <partenaire.icon className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">{partenaire.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{partenaire.description}</p>
                    {partenaire.note && (
                      <p className="text-xs text-amber-600 mt-2 italic flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {partenaire.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Note importante */}
        <section className="mb-12">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl">
            <h3 className="font-bold text-blue-800 mb-2">Séparation des rôles</h3>
            <p className="text-blue-700 text-sm leading-relaxed">
              La FTPE Outremer veille à la stricte séparation entre son rôle syndical
              et les activités commerciales ou opérationnelles de ses partenaires.
              Elle n'assure ni la vente, ni l'exploitation des services proposés par l'écosystème.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/adhesion-ftpe-workflow')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 text-lg rounded-xl"
          >
            Rejoindre l'écosystème
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 text-center">
        <p className="font-semibold">FTPE Outremer – Syndicat patronal ultramarin</p>
        <p className="text-slate-400 text-sm mt-2">Facilitation • Responsabilité • Développement territorial</p>
      </footer>
    </div>
  );
}
